/**
 * Zod schemas for API request/response validation
 */

import { z } from 'zod';

import { base64urlWithMax } from '../config/schemas';
import { MAX_BLINDED_BASE64_LENGTH } from '../constants';

// ============================================================================
// OPRF Request Schema
// ============================================================================

/**
 * OPRF request validation schema
 * Minimal design: only blinded element and key ID
 */
export const oprfRequestSchema = z.object({
  blinded_b64: base64urlWithMax(MAX_BLINDED_BASE64_LENGTH),
});

export type OprfRequestValidated = z.infer<typeof oprfRequestSchema>;

// ============================================================================
// OPRF Response Schemas
// ============================================================================

/**
 * OPRF response data schema
 */
export const oprfResponseDataSchema = z.object({
  evaluated_b64: z.string().regex(/^[A-Za-z0-9_-]+$/),
  proof_b64: z.string().regex(/^[A-Za-z0-9_-]+$/),
  server_pubkey_b64: z.string().regex(/^[A-Za-z0-9_-]+$/),
  turnstile_required: z.boolean(),
});

/**
 * OPRF success response schema
 */
export const oprfSuccessResponseSchema = z.object({
  success: z.literal(true),
  data: oprfResponseDataSchema,
});

/**
 * OPRF error response schema
 */
export const oprfErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z
    .object({
      code: z.string(),
      // Additional error fields are dynamic based on error type
    })
    .passthrough(),
});

/**
 * Combined OPRF response schema
 */
export const oprfResponseSchema = z.discriminatedUnion('success', [
  oprfSuccessResponseSchema,
  oprfErrorResponseSchema,
]);

// ============================================================================
// Setup and Stage Fetch Schemas
// ============================================================================

export const setupResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    initial: z.object({
      salt: z.string(),
      iv: z.string(),
      kdf_params: z.object({
        hint: z.string(),
        minMemoryMiB: z.number().int().min(16),
        memoryMiB: z.number().int().min(16).optional(),
        timeCost: z.number().int().min(1).optional(),
        parallelism: z.number().int().min(1).optional(),
      }),
      stage0_token: z.string(),
    }),
    voprf: z.object({
      public_key_b64: z.string(),
    }),
    turnstile: z.object({ site_key: z.string() }),
  }),
});

// Note: SetupResponse type is defined in types.ts for consistency with other response interfaces

export const stageFetchResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ ciphertext: z.string() }),
});

export type StageFetchResponse = z.infer<typeof stageFetchResponseSchema>;

// ============================================================================
// Generic Schemas
// ============================================================================

/**
 * Generic success response schema
 */
// Removed generic API schema helpers as they are unused

/**
 * Generic error response schema
 */
// Removed unused apiErrorSchema

/**
 * Generic API response schema factory
 */
// Removed unused createApiResponseSchema
